import type { Dispatch, FC, ReactNode, SetStateAction } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useDebounce } from 'src/hooks';
import type {
  DetailedFollowUpTicket,
  FollowUpTicketListItem,
} from 'src/types/followUp';
import type { SurveyDetail } from 'src/types/survey';

import { useFilterContext } from '../app/context/FilterContext';
import { useSurveyDetailQuery } from '../survey/SurveySettings/api';

import useOnCurrentResultFilterChange from './hooks/useOnCurrentResultFilterChange';
import useTicketDetailQuery from './hooks/useTicketDetailQuery';
import useTicketListQuery from './hooks/useTicketListQuery';

export interface TicketTableContextValue {
  detailIsLoading: boolean;
  listIsLoading: boolean;
  listError: string | null;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  tickets?: Array<FollowUpTicketListItem>;
  totalTickets?: number;
  sortBy: string;
  setSortBy: Dispatch<SetStateAction<string>>;
  searchString: string;
  debouncedSearchString: string;
  setSearchString: Dispatch<SetStateAction<string>>;
  selectedTicket?: DetailedFollowUpTicket;
  refreshTickets: () => Promise<any>;
  refreshSelectedTicket: () => Promise<any>;
  survey?: SurveyDetail;
}

const initialContextValue: TicketTableContextValue = {
  detailIsLoading: false,
  listIsLoading: false,
  listError: null,
  page: 0,
  setPage: () => {},
  tickets: undefined,
  totalTickets: 0,
  searchString: '',
  debouncedSearchString: '',
  setSearchString: () => {},
  selectedTicket: undefined,
  sortBy: 'id',
  setSortBy: () => {},
  refreshTickets: async () => {},
  refreshSelectedTicket: async () => {},
};

export const TicketFilterContext =
  createContext<TicketTableContextValue>(initialContextValue);

const TicketFilterProvider: FC<{
  children?: ReactNode;
  disableListQuery?: boolean;
}> = ({ children, disableListQuery = false }) => {
  const { t } = useTranslation('common');
  const selectedTicketId = new URLSearchParams(useLocation().search).get(
    // replace with useSearchParams after update to react router 6
    'ticket',
  );
  const { filterGroups } = useFilterContext();
  const [page, setPage] = useState(1);
  const [searchString, setSearchString] = useState<string>('');
  const debouncedSearchString = useDebounce(searchString);
  const [sortBy, setSortBy] = useState('-id');

  const {
    data: selectedTicket,
    refetch: refreshSelectedTicket,
    isFetching: detailIsLoading,
  } = useTicketDetailQuery(selectedTicketId ?? undefined);

  const {
    data,
    refetch: refreshTickets,
    isFetching: listIsLoading,
    error,
  } = useTicketListQuery(
    page,
    debouncedSearchString,
    sortBy,
    !disableListQuery,
  );
  const listError = useMemo(() => {
    if (error === 'somethingWentWrong') {
      return t('somethingWentWrong');
    }
    return error || '';
  }, [error]);
  const tickets = data?.results;
  const totalTickets = data?.count;
  const { data: survey } = useSurveyDetailQuery();

  useOnCurrentResultFilterChange(filterGroups, () => setPage(1));

  return (
    <TicketFilterContext.Provider
      value={{
        detailIsLoading,
        listIsLoading,
        listError,
        page,
        setPage,
        tickets,
        totalTickets,
        searchString,
        debouncedSearchString,
        setSearchString,
        selectedTicket,
        sortBy,
        setSortBy,
        refreshTickets,
        refreshSelectedTicket,
        survey,
      }}
    >
      {children}
    </TicketFilterContext.Provider>
  );
};

export const useTicketTableContext = () => useContext(TicketFilterContext);

export default TicketFilterProvider;
